/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {loadAllGroups} from '@ion/core/src/services/Connect';
import {GenericObject, Group} from '@ion/core/src/types/entities';
import {alphaSort} from '@ion/core/src/utils/SortUtils';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseLoadGroupsResult = UseDefaultQueryResult & {
  groups: Group[] | null;
};

export interface UseLoadGroupsParams {
  accountId: number;
  sort?: boolean;
};

/**
 * The useLoadGroups hook provides a React Query hook for referencing groups (not accounts).
 *
 * ```typescript
 *
 * import {useLoadGroups} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {groups} = useLoadGroups();
 *
 * ```
 *
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @returns UseLoadGroupsResult containing the query results and the groups.
 */
export const useLoadGroups = (
  {accountId, sort = false}: UseLoadGroupsParams,
  enabled = true,
  staleTime = getStaleTime(1, false, false)
): UseLoadGroupsResult => {
  const query = useQuery([QueryKey.LOAD_GROUPS, accountId], async() => {
    const results = await loadAllGroups({
      pageId   : 1,
      pageSize : 100,
      accountId
    }) as Group[];
    if (sort) {
      return alphaSort({items: results as unknown as GenericObject[]}) as unknown as Group[];
    }
    return results;
  }, {staleTime: staleTime as number, enabled});

  return createQueryResult(query, {
    groups : query.data || null
  }) as UseLoadGroupsResult;
};

export default useLoadGroups;
